import { useState } from "react"
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router"
import { auth } from "@/lib/auth"
import { KeyRoundIcon } from "lucide-react"
import { passkeysSupported, signInWithPasskey } from "@/lib/webauthn"
import { Button, Input, Spinner, Toast } from "@/components/ui"
import { useT } from "@/lib/i18n"
import { LegalLinks } from "@/components/LegalLinks"

export const Route = createFileRoute("/login")({
  beforeLoad: ({ context }) => {
    if (context.auth.isLoggedIn()) {
      throw redirect({ to: "/chat" })
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const t = useT()
  const nav = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [totp, setTotp] = useState("")
  const [needTotp, setNeedTotp] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function withPasskey() {
    setBusy(true)
    setError(null)
    try {
      const tokens = await signInWithPasskey(email.trim() || undefined)
      await auth.loginWithTokens(tokens)
      await nav({ to: "/chat" })
    } catch (e) {
      const msg = (e as Error).message
      if (msg !== "cancelled") setError(msg)
    } finally {
      setBusy(false)
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await auth.login(email, password, totp || undefined)
      await nav({ to: "/chat" })
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err.code === "totp_required") {
        setNeedTotp(true)
        setError("Enter your 2FA code to finish signing in.")
      } else {
        setError(err.message)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative min-h-screen grid place-items-center overflow-hidden px-4 bg-background">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[20%] top-[15%] h-72 w-72 animate-blob-1 rounded-full bg-primary/20 blur-[100px]" />
        <div className="absolute right-[18%] bottom-[18%] h-64 w-64 animate-blob-2 rounded-full bg-violet/15 blur-[100px]" />
      </div>
      <form
        onSubmit={onSubmit}
        className="relative z-10 w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card/90 p-7 shadow-glass backdrop-blur"
      >
        <div className="mb-5 flex flex-col items-center text-center">
          <img
            src="/lucy-logo.png"
            alt="Lucy"
            className="mb-3 h-14 w-14 rounded-full object-cover shadow-[0_4px_18px_rgba(192,140,255,0.5)]"
          />
          <h1 className="text-xl font-semibold tracking-tight">
            {t("auth.signinTitle")}
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">{t("auth.welcomeBack")}</p>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("auth.email")}
          </label>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t("auth.password")}
          </label>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {needTotp && (
          <div className="space-y-2">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("auth.totp")}
            </label>
            <Input
              inputMode="numeric"
              autoFocus
              placeholder="123 456"
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
            />
          </div>
        )}
        {error && <Toast variant="destructive">{error}</Toast>}
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? <Spinner /> : null}
          {t("auth.signin")}
        </Button>
        {passkeysSupported() && (
          <Button type="button" variant="outline" disabled={busy} className="w-full" onClick={withPasskey}>
            <KeyRoundIcon className="h-3.5 w-3.5" /> {t("auth.passkey")}
          </Button>
        )}
        <div className="flex items-center justify-between text-xs">
          <Link to="/forgot" className="text-muted-foreground hover:text-foreground">
            {t("auth.forgot")}
          </Link>
          <Link to="/signup" className="text-muted-foreground hover:text-foreground">
            {t("auth.createAccount")}
          </Link>
        </div>
        <div className="flex items-center justify-center gap-3 pt-1 text-[11px] text-muted-foreground">
          <Link to="/pricing" className="hover:text-foreground">{t("pricing.title")}</Link>
          <span aria-hidden>·</span>
          <LegalLinks />
        </div>
      </form>
    </div>
  )
}
